/**
 * bfm-story-animate.js
 *
 * Strategy:
 *   - .story-board.for-animate  — initial collapsed state is set entirely via
 *     gsap.set() in JS; no CSS class is required for the starting position.
 *   - .story-board.for-size     — permanently expanded off-screen clone used
 *     to read real DOM measurements (no snapshot/restore needed).
 *   - GSAP ScrollTrigger animates from the gsap.set() init state to the final
 *     expanded state using values read from for-size.
 *
 * GSAP is loaded via CDN as a global (window.gsap / window.ScrollTrigger).
 */

function checkAni(el, color) {
  el.animate(
    {
      transform: [
        'translateX(0)',
        'translateX(-10px)',
        'translateX(10px)',
        'translateX(-6px)',
        'translateX(6px)',
        'translateX(-3px)',
        'translateX(3px)',
        'translateX(0)',
      ],
    },
    { duration: 500, easing: 'cubic-bezier(.36, .07, .19, .97)' },
  );
  if (color) {
    el.animate(
      { color: [color, color, color, ''] },
      { duration: 1800, easing: 'linear' },
    );
  }
}

// Global store for submitted message codes; cleared after registration
window.__BFM_PENDING_MESSAGE_CODES__ = window.__BFM_PENDING_MESSAGE_CODES__ || [];

(function () {
  'use strict';

  // Wait for GSAP globals to be available
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.warn('[bfm-story-animate] GSAP or ScrollTrigger not found. Aborting.');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true, autoRefreshEvents: "visibilitychange,DOMContentLoaded,load" });


  // ─── DOM references ───────────────────────────────────────────────────────────

  const storySection = document.querySelector(".bfm-story-section");
  const boardAnimate = document.querySelector(".bfm-story-section .story-board.for-animate");
  const boardSize = document.querySelector(".bfm-story-section .story-board.for-size");

  if (!storySection || !boardAnimate || !boardSize) {
    console.warn("[bfm-story-animate] Required elements not found. Aborting.");
    return;
  }

  // ─── Design constants ─────────────────────────────────────────────────────────

  const designBoard = { width: 1360, height: 816 };

  const designLayout = {
    commentInitialHeight: 188,
    headlineColumnWidth: 628,
    sideColumnWidth: 590,
    columnGap: 142,
    headlineBodyMarginTop: 8,
    countdownMarginTop: 16,
    consentMarginTop: 10,
    promptExpandedWidth: 580,
    promptExpandedHeight: 184,
    promptInputHeight: 96,
    promptInputLineHeight: 24,
    promptButtonWidth: 214,
    promptButtonHeight: 48,
    boardInsightHeight: 127,
    boardInsightPaddingY: 24,
    rowGap: 80,
  };

  const mobileLayout = {
    headlineBodyMarginTop: 8,
    countdownMarginTop: 16,
    consentMarginTop: 9,
    promptExpandedHeight: 120,
    promptInputHeight: 48,
    promptInputLineHeight: 24,
    promptButtonWidth: 188,
    promptButtonHeight: 40,
    rowGap: 24,
    boardInsightHeight: 127,
    boardInsightPaddingY: 24,
  };

  // ─── Utilities ────────────────────────────────────────────────────────────────

  const isMobile = () => innerWidth < 768;
  const isMedium = () => !isMobile() && innerWidth < 1024;

  const getBoardScale = () =>
    boardAnimate ? Math.min(boardAnimate.clientWidth / designBoard.width, 1) : 1;

  const scale = (v) => v * getBoardScale();
  const responsive = (mob, desk) => isMobile() ? mob : scale(desk);

  function debounce(fn, delay) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
  }

  // ─── Measurement helpers (read from for-size) ─────────────────────────────────

  const getExpandedSideWidth = () => {
    if (isMobile()) return "0px";
    return `${scale(designLayout.sideColumnWidth)}px`;
  };

  const getExpandedColumnGap = () =>
    isMobile() ? 0 : isMedium() ? 40 : scale(designLayout.columnGap);

  const getExpandedRowGap = () => {
    if (isMobile()) return mobileLayout.rowGap;
    if (isMedium()) return 32;
    const w = innerWidth;
    if (w <= 1200) return 54;
    return designLayout.rowGap;
  };

  const getHeadlineBodyMarginTop = () =>
    responsive(mobileLayout.headlineBodyMarginTop, designLayout.headlineBodyMarginTop);

  const getCountdownMarginTop = () =>
    responsive(mobileLayout.countdownMarginTop, designLayout.countdownMarginTop);

  const getConsentMarginTop = () =>
    responsive(mobileLayout.consentMarginTop, designLayout.consentMarginTop);

  const getPromptInputHeight = () =>
    responsive(mobileLayout.promptInputHeight, designLayout.promptInputHeight);

  // const getPromptInputLineHeight = () =>
  //   responsive(mobileLayout.promptInputLineHeight, designLayout.promptInputLineHeight);

  const getPromptButtonHeight = () =>
    responsive(mobileLayout.promptButtonHeight, designLayout.promptButtonHeight);

  const getBoardInsightPaddingY = () =>
    responsive(mobileLayout.boardInsightPaddingY, designLayout.boardInsightPaddingY);

  const getExpandedCommentHeight = () => {
    const el = boardSize.querySelector(".board-comment");
    return el ? Math.ceil(el.scrollHeight) : designLayout.commentInitialHeight;
  };

  const getMobileExpandedCommentMarginTop = () => {
    const el = boardSize.querySelector(".board-comment");
    if (!el) return 0;
    return Math.max(0, innerHeight / 2 - Math.ceil(el.offsetHeight) / 2);
  };

  const getMobileCommentMarginTop = () => {
    const comment = boardAnimate.querySelector(".board-comment");
    if (!comment) return 0;
    return Math.max(0, innerHeight / 2 - comment.offsetHeight / 2);
  };

  const getHeadlineBodyHeight = () => {
    const el = boardSize.querySelector(".headline-body");
    return el ? Math.ceil(Math.max(el.scrollHeight, el.offsetHeight)) : 0;
  };

  const getCountdownHeight = () => {
    const el = boardSize.querySelector(".countdown");
    return el ? Math.ceil(Math.max(el.scrollHeight, el.offsetHeight)) : 0;
  };

  const getConsentHeight = () => {
    const el = boardSize.querySelector(".headline-consent");
    return el ? Math.ceil(Math.max(el.scrollHeight, el.offsetHeight)) : 0;
  };

  const getPromptExpandedWidth = () =>
    isMobile() ? "100%" : `${scale(designLayout.promptExpandedWidth)}px`;

  const getPromptExpandedHeight = () =>
    isMobile() ? mobileLayout.promptExpandedHeight : scale(designLayout.promptExpandedHeight);

  const getPromptButtonExpandedWidth = () => {
    const btn = boardSize.querySelector(".story-prompt button");
    if (!btn) return isMobile() ? mobileLayout.promptButtonWidth : scale(designLayout.promptButtonWidth);
    return Math.ceil(Math.max(btn.scrollWidth, btn.offsetWidth));
  };

  const getPromptActionLabelWidth = () => {
    const el = boardSize.querySelector(".story-prompt-action-label");
    if (!el) return 80;
    const prev = el.style.width;
    el.style.removeProperty("width");
    const w = Math.ceil(el.scrollWidth || el.offsetWidth);
    el.style.width = prev;
    return w || 80;
  };

  const getBoardInsightHeight = () =>
    isMobile() ? mobileLayout.boardInsightHeight : isMedium() ? 96 : scale(designLayout.boardInsightHeight);

  // ─── Centering offsets (for-animate) ─────────────────────────────────────────

  const getTitleCenterOffset = () => {
    const comment = boardAnimate.querySelector(".board-comment");
    const title = boardAnimate.querySelector(".headline-title");
    return (comment && title) ? (comment.offsetWidth - title.offsetWidth) / 2 : 0;
  };

  const getTitleFirstLineCenterOffset = () => {
    const title = boardAnimate.querySelector(".headline-title");
    const firstLine = boardAnimate.querySelector(".title-first-line");
    return (title && firstLine) ? (title.offsetWidth - firstLine.offsetWidth) / 2 : 0;
  };

  const getPromptCenterOffset = () => {
    const comment = boardAnimate.querySelector(".board-comment");
    const prompt = boardAnimate.querySelector(".story-prompt");
    return (comment && prompt) ? (comment.offsetWidth - prompt.offsetWidth) / 2 : 0;
  };

  // Desktop only: translate board-comment from its centred position (for-animate,
  // justify-self:center) to the left-aligned final position (for-size, justify-self:start).
  // Uses offsetLeft relative to each board's own board-block to avoid the 100vw
  // off-screen offset on for-size affecting getBoundingClientRect results.
  const getCommentCenterOffsetX = () => {
    if (isMobile()) return 0;
    const sizeBlock = boardSize.querySelector(".board-block");
    const sizeComment = boardSize.querySelector(".board-comment");
    const animBlock = boardAnimate.querySelector(".board-block");
    const animComment = boardAnimate.querySelector(".board-comment");
    if (!sizeBlock || !sizeComment || !animBlock || !animComment) return 0;
    const sizeLeft = sizeComment.offsetLeft - sizeBlock.offsetLeft;
    const animLeft = animComment.offsetLeft - animBlock.offsetLeft;
    return animLeft - sizeLeft;
  };

  // ─── Bubble layout ────────────────────────────────────────────────────────────

  const mutedBubbleRatio = 0.3;

  function pos(base, overrides) {
    overrides = overrides || {};
    return {
      480: overrides[480] !== undefined ? overrides[480] : base,
      768: overrides[768] !== undefined ? overrides[768] : base,
      1024: overrides[1024] !== undefined ? overrides[1024] : base,
      1200: overrides[1200] !== undefined ? overrides[1200] : base,
    };
  }

  const bubblePositions = [
    pos({ right: 58.6, top: 56.7, maxWidth: 480 }, { 480: { right: 53.2, top: 30.55 }, 768: { left: -2, top: 65 } }),
    pos({ left: 69.1, top: 43.5, maxWidth: 400 }, { 480: { right: 44.5, top: 62 }, 768: { left: 72, top: 36 } }),
    pos({ right: 68.5, top: 42.4, maxWidth: 400 }, { 480: { left: 31.1, top: 19.5 }, 768: { left: -1, top: 25 } }),
    pos({ right: 52.7, top: 28.2, maxWidth: 480 }, { 480: { left: 60.2, top: 69 }, 768: { left: 8, top: -5 } }),
    pos({ left: 58.7, top: 36.8 }, { 480: { right: 29.1, top: 77.2 }, 768: { left: 68, top: 6 } }),
    pos({ left: 53.2, top: 71.1 }, { 480: { left: 1, top: 8.9 }, 768: { left: 54, top: 76 } }),
    pos({ right: 67.3, top: 72.2 }, { 480: { left: 64, top: 35.6 }, 768: { left: -6, top: 88 } }),
    pos({ left: 53.2, top: 16.2 }, { 480: { left: 45.8, top: 90 }, 768: { left: 50, top: -28 } }),
    pos({ left: 39.4, top: 75.5 }, { 480: { left: 46.8, top: 49.5, maxWidth: 202 }, 768: { left: 37, top: 110 } }),
  ];

  function randomizeMutedBubbles(bubbles) {
    if (!bubbles.length) return;
    const count = Math.max(1, Math.round(bubbles.length * mutedBubbleRatio));
    const shuffled = gsap.utils.shuffle([...bubbles]);
    const muted = new Set(shuffled.slice(0, count));
    bubbles.forEach((b) => b.classList.toggle("muted", muted.has(b)));
  }

  function syncBubbleWidths(bubbles, bp) {
    if (bp !== 480 || !storySection) {
      bubbles.forEach((b, i) => {
        const p = bubblePositions[i] && bubblePositions[i][bp];
        if (!p) return;
        if (p.width !== undefined) {
          b.style.width = `${p.width}px`;
          b.style.removeProperty("max-width");
        } else if (p.maxWidth !== undefined) {
          b.style.removeProperty("width");
          b.style.maxWidth = `${p.maxWidth}px`;
        } else {
          b.style.removeProperty("width");
          b.style.removeProperty("max-width");
        }
      });
      return;
    }

    const sectionWidth = storySection.clientWidth;
    const maxWidth = Math.min(320, Math.max(0, sectionWidth - 32));
    const defaultWidth = Math.min(maxWidth, Math.max(180, Math.round(sectionWidth * 0.52)));

    bubbles.forEach((bubble, i) => {
      const configured = bubblePositions[i] && bubblePositions[i][bp];
      if (configured && configured.width !== undefined) {
        bubble.style.width = `${configured.width}px`;
        bubble.style.removeProperty("max-width");
        return;
      }
      if (configured && configured.maxWidth !== undefined) {
        bubble.style.removeProperty("width");
        bubble.style.maxWidth = `${configured.maxWidth}px`;
        return;
      }

      const text = bubble.querySelector("p");
      if (!text) { bubble.style.width = `${defaultWidth}px`; bubble.style.removeProperty("max-width"); return; }

      const style = getComputedStyle(bubble);
      const hPad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
        + parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
      const prevW = bubble.style.width;
      const prevWS = text.style.whiteSpace;
      const prevMH = text.style.maxHeight;

      bubble.style.removeProperty("width");
      text.style.whiteSpace = "nowrap";
      text.style.maxHeight = "none";
      const natural = Math.ceil(text.scrollWidth + hPad);
      bubble.style.width = prevW;
      text.style.whiteSpace = prevWS;
      text.style.maxHeight = prevMH;

      bubble.style.width = `${Math.round(Math.max(defaultWidth, Math.min(natural, maxWidth)))}px`;
      bubble.style.removeProperty("max-width");
    });
  }

  function positionBubbles(bubbles) {
    if (!bubbles.length) return;

    const w = innerWidth;
    const bp = w < 768 ? 480 : w < 1024 ? 768 : w <= 1200 ? 1024 : 1200;

    syncBubbleWidths(bubbles, bp);

    bubbles.forEach((bubble, i) => {
      const position = bubblePositions[i] && bubblePositions[i][bp];
      if (!position) return;
      if (position.right !== undefined) {
        bubble.style.removeProperty("left");
        bubble.style.right = `${position.right}%`;
      } else {
        bubble.style.removeProperty("right");
        bubble.style.left = `${position.left}%`;
      }
      bubble.style.top = `${position.top}%`;
    });

    if (innerWidth >= 768) {
      bubbles.forEach((bubble) => {
        const p = bubble.querySelector("p");
        if (!p) return;
        const prevMH = p.style.maxHeight;
        p.style.maxHeight = "none";
        const fullHeight = p.scrollHeight;
        p.style.maxHeight = prevMH;

        const collapsedHeight = parseFloat(getComputedStyle(bubble).getPropertyValue("--collapsed-text-height")) || 48;
        const isExpandable = fullHeight > collapsedHeight + 2;
        bubble.classList.toggle("is-expandable", isExpandable);
        if (isExpandable) {
          bubble.style.setProperty("--expanded-text-height", `${fullHeight}px`);
        } else {
          bubble.style.removeProperty("--expanded-text-height");
        }
      });
    }
  }

  function setupBubbleDialog(bubbles) {
    const isMobileDialog = () => matchMedia("(max-width: 767px)").matches;
    const bubbleDialog = document.querySelector("[data-dsm-dialog='bubble-dialog']");
    const dialogBody = document.querySelector("[data-bubble-dialog-body]");

    const openDialog = (bubble) => {
      if (!isMobileDialog() || !bubbleDialog || !dialogBody) return;
      dialogBody.textContent = bubble.textContent ? bubble.textContent.trim() : "";
      const ctrl = window.DsmDialog;
      if (ctrl) { ctrl.open("bubble-dialog"); return; }
      bubbleDialog.classList.add("is-open");
      document.documentElement.classList.add("dsm-scroll-lock");
      bubbleDialog.querySelector(".dsm-dialog__close") && bubbleDialog.querySelector(".dsm-dialog__close").focus();
    };

    bubbles.forEach((b) => {
      b.setAttribute("role", "button");
      b.setAttribute("tabindex", "0");
      b.setAttribute("aria-haspopup", "dialog");
      b.addEventListener("click", () => openDialog(b));
      b.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if (!isMobileDialog()) return;
        e.preventDefault();
        openDialog(b);
      });
    });
  }

  // ─── Section height sync ──────────────────────────────────────────────────────

  const syncSectionHeight = () => {
    const boardRect = boardSize.getBoundingClientRect();
    if (isMobile()) {
      const extraMargin = getMobileCommentMarginTop();
      storySection.style.minHeight = `${boardRect.height + extraMargin}px`;
    } else {
      storySection.style.minHeight = `${Math.max(innerHeight, boardRect.height)}px`;
    }
  };

  // ─── Main init ────────────────────────────────────────────────────────────────

  function initStoryAnimate() {
    if (!storySection || !boardAnimate || !boardSize) return;

    syncSectionHeight();

    const q = (sel) => boardAnimate.querySelector(sel);
    const qa = (sel) => gsap.utils.toArray(sel, boardAnimate);

    const bubbles = gsap.utils.toArray(".bubble", storySection.querySelector(".bg"));
    const commentBoard = q(".board-comment");
    const headlineTitle = q(".headline-title");
    const titleFirstLine = q(".title-first-line");
    const regularText = qa(".headline-title .regular");
    const storyPrompt = q(".story-prompt");
    const promptInput = q(".story-prompt-placeholder");
    const promptBtn = q(".story-prompt button");
    const promptActionLabel = q(".story-prompt-action-label");
    const headlineBody = q(".headline-body");
    const rulesBtn = q(".headline-rules-btn");
    const countdown = q(".countdown");
    const consent = q(".headline-consent");
    const sidePanel = q(".story-side-panel");
    const boardInsight = q(".board-insight");
    const boardBlock = q(".board-block");

    randomizeMutedBubbles(bubbles);
    positionBubbles(bubbles);

    const getInitPromptHeight = () => {
      const w = innerWidth;
      if (w < 768) return 56;
      if (w < 1024) return 40;
      if (w <= 1200) return 48;
      return 56;
    };
    const getInitPromptButtonWidth = () => {
      const w = innerWidth;
      if (w < 768) return 40;
      if (w < 1024) return 32;
      if (w <= 1200) return 38;
      return 40;
    };

    gsap.set(boardAnimate, { opacity: 1 });
    if (boardBlock) gsap.set(boardBlock, { gap: 0, "--side-width": "0px", y: 0 });
    gsap.set(headlineTitle, { autoAlpha: 0, y: 52, x: getTitleCenterOffset });
    gsap.set(titleFirstLine, { x: getTitleFirstLineCenterOffset });
    gsap.set(regularText, { fontWeight: 400 });
    gsap.set(headlineBody, { height: 0, marginTop: 0, autoAlpha: 0, y: 16 });
    gsap.set(countdown, { height: 0, marginTop: 0, autoAlpha: 0, y: 16 });
    if (rulesBtn) gsap.set(rulesBtn, { autoAlpha: 0, y: 8, marginTop: 0 });
    gsap.set(consent, { maxHeight: 0, marginTop: 0, autoAlpha: 0, y: 16 });
    gsap.set(storyPrompt, {
      autoAlpha: 0, x: getPromptCenterOffset,
      width: getInitPromptHeight,
      height: getInitPromptHeight,
      borderRadius: "99px", padding: 0,
      pointerEvents: "none",
    });
    gsap.set(promptInput, { autoAlpha: 0, height: 24 });
    gsap.set(promptBtn, {
      gap: 0, padding: 0, width: getInitPromptButtonWidth,
      background: "linear-gradient(95deg, rgba(231,174,178,0.49) -2.25%, rgba(174,60,98,0.49) 103.52%)",
      boxShadow: "0 4px 46.6px rgba(232,210,212,0.29)",
      border: "1px solid #F4E7E8",
    });
    gsap.set(promptActionLabel, { autoAlpha: 0, width: 0 });
    if (isMobile()) {
      gsap.set(sidePanel, { autoAlpha: 0, y: 40 });
    } else {
      gsap.set(sidePanel, { autoAlpha: 0, height: "100%", clipPath: "inset(50% 0)", y: 0, overflow: "hidden" });
    }
    if (isMobile()) {
      gsap.set(boardInsight, { autoAlpha: 0, y: 40 });
    } else {
      gsap.set(boardInsight, { autoAlpha: 0, y: 24, height: 0, paddingTop: 0, paddingBottom: 0, overflow: "hidden" });
    }
    gsap.set(bubbles, { autoAlpha: 0, y: 18, scale: 0.82, transformOrigin: "center center", pointerEvents: "auto" });
    if (commentBoard) gsap.set(commentBoard, { pointerEvents: "none", minHeight: 0, x: 0 });
    if (boardInsight) gsap.set(boardInsight, { pointerEvents: "none" });

    const entrance = gsap.timeline({
      defaults: { ease: "none" },
      onComplete: () => {
        storySection.classList.add("story-board-animate-complete");
      },
      scrollTrigger: {
        trigger: storySection,
        start: "top top",
        end: "+=100%",
        pin: true,
        scrub: 0.8,
        invalidateOnRefresh: true,
        once: true,
      },
    });

    const set = (el, vars, pos) => {
      if (!el) return;
      const targets = Array.isArray(el) ? el.filter(Boolean) : [el];
      if (targets.length) entrance.set(targets, vars, pos);
    };
    const to = (el, vars, pos) => {
      if (!el) return;
      const targets = Array.isArray(el) ? el.filter(Boolean) : [el];
      if (targets.length) entrance.to(targets, vars, pos);
    };
    const fto = (el, fv, tv, pos) => {
      if (!el) return;
      const targets = Array.isArray(el) ? el.filter(Boolean) : [el];
      if (targets.length) entrance.fromTo(targets, fv, tv, pos);
    };

    // Hard-reset at t=0
    if (boardBlock) set(boardBlock, { gap: 0, "--side-width": "0px", y: 0 }, 0);
    set(bubbles, { autoAlpha: 0, y: 18, scale: 0.82, pointerEvents: "auto" }, 0);
    set(headlineTitle, { autoAlpha: 0, y: 52, x: getTitleCenterOffset }, 0);
    set(titleFirstLine, { x: getTitleFirstLineCenterOffset }, 0);
    set(regularText, { fontWeight: 400 }, 0);
    set(headlineBody, { height: 0, marginTop: 0, autoAlpha: 0, y: 16 }, 0);
    set(countdown, { height: 0, marginTop: 0, autoAlpha: 0, y: 16 }, 0);
    if (rulesBtn) set(rulesBtn, { autoAlpha: 0, y: 8, marginTop: 0 }, 0);
    set(consent, { maxHeight: 0, marginTop: 0, autoAlpha: 0, y: 16 }, 0);
    set(storyPrompt, { autoAlpha: 0, x: getPromptCenterOffset, width: getInitPromptHeight, height: getInitPromptHeight, borderRadius: "99px", padding: 0, pointerEvents: "none" }, 0);
    set(promptInput, { autoAlpha: 0, height: 24 }, 0);
    set(promptBtn, { gap: 0, padding: 0, width: getInitPromptButtonWidth }, 0);
    set(promptActionLabel, { autoAlpha: 0, width: 0 }, 0);
    if (commentBoard) set(commentBoard, {
      minHeight: 0,
      width: "",
      x: 0,
      marginTop: () => isMobile() ? getMobileCommentMarginTop() : "",
    }, 0);
    if (isMobile()) {
      set(sidePanel, { autoAlpha: 0, y: 40 }, 0);
    } else {
      set(sidePanel, { autoAlpha: 0, height: "100%", clipPath: "inset(50% 0)", y: 0 }, 0);
    }
    if (isMobile()) {
      set(boardInsight, { autoAlpha: 0, y: 40 }, 0);
    } else {
      set(boardInsight, { autoAlpha: 0, y: 24, height: 0, paddingTop: 0, paddingBottom: 0 }, 0);
    }

    // Bubbles entrance
    fto(bubbles,
      { autoAlpha: 0, y: 18, scale: 0.82 },
      { autoAlpha: 1, y: 0, scale: 1, duration: 1, ease: "back.out(1.8)", stagger: { each: 0.18, from: "start" } },
      0);

    // Headline fades in while still centred
    to(headlineTitle, { autoAlpha: 1, y: 52, x: getTitleCenterOffset, duration: 0.8 }, ">+0.18");

    // Bubbles exit
    to(bubbles, { autoAlpha: 0, y: -8, scale: 1.12, duration: 0.8, ease: "back.in(1.2)", stagger: 0.04 }, ">+0.12");
    set(bubbles, { pointerEvents: "none" });
    set(commentBoard, { pointerEvents: "auto" });

    // Headline drops to baseline; prompt slides in
    to(headlineTitle, { y: 0, x: getTitleCenterOffset, duration: 0.7 }, "<+0.15");
    to(storyPrompt, { autoAlpha: 1, duration: 0.2 }, "<+0.1");
    to(storyPrompt, { width: "100%", borderRadius: 28, x: 0, duration: 0.7 }, "<+0.1");
    entrance.addLabel("promptWidthOpen", ">");
    to([promptInput], { autoAlpha: 1, duration: 0.35 }, "<+0.22");

    // Headline aligns left
    if (isMobile()) {
      to(headlineTitle, { x: 0, duration: 0.8 }, "promptWidthOpen+=0.18");
    } else {
      to(commentBoard, { x: 0, duration: 0.8 }, "promptWidthOpen+=0.18");
      to(headlineTitle, { x: 0, duration: 0.8 }, "<");
    }
    to(regularText, { fontWeight: 600, duration: 0.8 }, "<");
    to(titleFirstLine, { x: 0, duration: 0.8 }, "<");
    to(storyPrompt, { width: getPromptExpandedWidth, duration: 0.8 }, "<");
    if (isMobile() && commentBoard) {
      to(commentBoard, { marginTop: getMobileExpandedCommentMarginTop, duration: 0.8 }, "<");
    }
    to(headlineBody, { height: getHeadlineBodyHeight, marginTop: getHeadlineBodyMarginTop, duration: 0.8 }, isMobile() ? "<" : ">");
    to(countdown, { height: getCountdownHeight, marginTop: getCountdownMarginTop, duration: 0.8 }, "<");
    to(consent, { maxHeight: getConsentHeight, marginTop: getConsentMarginTop, duration: 0.8 }, "<");

    to(boardBlock, {
      "--side-width": getExpandedSideWidth,
      columnGap: getExpandedColumnGap,
      rowGap: getExpandedRowGap,
      duration: 0.9,
    }, "<");

    to(storyPrompt, {
      height: getPromptExpandedHeight,
      borderRadius: () => isMobile() ? 16 : undefined,
      padding: "8px 8px 8px 24px",
      duration: 0.9,
    }, "<");

    if (!isMobile()) {
      to(commentBoard, {
        width: () => boardBlock ? boardBlock.clientWidth : (commentBoard ? commentBoard.clientWidth : designBoard.width),
        minHeight: getExpandedCommentHeight,
        duration: 0.9,
      }, "<");
    }

    to(headlineBody, { autoAlpha: 1, y: 0, duration: 0.6 }, ">");
    if (rulesBtn) to(rulesBtn, { autoAlpha: 1, y: 0, marginTop: 8, duration: 0.6 }, "<+0.06");
    if (isMobile()) {
      to(promptBtn, { width: getPromptButtonExpandedWidth, duration: 0.6 }, "<");
      to(promptActionLabel, { width: getPromptActionLabelWidth, autoAlpha: 1, duration: 0.6 }, "<");
    }
    to(countdown, { autoAlpha: 1, y: 0, duration: 0.6 }, "<+0.06");
    to(consent, { autoAlpha: 1, y: 0, duration: 0.6 }, "<+0.06");

    set(promptInput, { width: () => "calc(100% - 64px)" }, "<");
    to(promptInput, {
      height: getPromptInputHeight,
      // lineHeight: () => `${getPromptInputLineHeight()}px`,
      duration: 0.6,
    }, "<");

    // Side panel entrance
    const enablePromptInput = () => {
      if (!storyPrompt) return;
      gsap.set(storyPrompt, { pointerEvents: "auto" });
      if (promptInput && !isMobile()) promptInput.focus();
    };
    const disablePromptInput = () => {
      if (!storyPrompt) return;
      gsap.set(storyPrompt, { pointerEvents: "none" });
      if (promptInput && document.activeElement === promptInput && !isMobile()) promptInput.blur();
    };

    if (isMobile()) {
      to(promptBtn, {
        height: getPromptButtonHeight,
        borderRadius: "99px",
        gap: "8px",
        padding: "0 16px",
        background: "linear-gradient(95deg, #E7AEB2 -2.25%, #9A3C5B 103.52%)",
        border: "1px solid #F4E7E8",
        boxShadow: "0 4px 46.6px 0 rgba(232, 210, 212, 0.29)",
        duration: 0.65,
        onComplete: enablePromptInput,
        onReverseComplete: disablePromptInput,
        onUpdate: function () {
          if (entrance.scrollTrigger && entrance.scrollTrigger.direction === -1) disablePromptInput();
        }
      }, "<+0.2");
    } else {
      to(promptBtn, {
        width: getPromptButtonExpandedWidth,
        height: getPromptButtonHeight,
        borderRadius: "99px",
        gap: "8px",
        padding: "0 16px",
        background: "linear-gradient(95deg, #E7AEB2 -2.25%, #9A3C5B 103.52%)",
        border: "1px solid #F4E7E8",
        boxShadow: "0 4px 46.6px 0 rgba(232, 210, 212, 0.29)",
        duration: 0.65,
        onComplete: enablePromptInput,
        onReverseComplete: disablePromptInput,
        onUpdate: function () {
          if (entrance.scrollTrigger && entrance.scrollTrigger.direction === -1) disablePromptInput();
        }
      }, "<+0.2");
    }

    if (!isMobile()) {
      to(promptActionLabel, { width: getPromptActionLabelWidth, autoAlpha: 1, duration: 0.4 }, "<+0.18");
    }

    if (isMobile()) {
      to(sidePanel, {
        autoAlpha: 1, y: 0, duration: 0.7,
        onStart: () => sidePanel && sidePanel.querySelectorAll("external-video").forEach((ev) => ev.preload && ev.preload()),
      }, ">+0.5");
    } else {
      to(sidePanel, {
        autoAlpha: 1, clipPath: "inset(0% 0)", duration: 0.7,
        onStart: () => sidePanel && sidePanel.querySelectorAll("external-video").forEach((ev) => ev.preload && ev.preload()),
      }, "<+0.12");
      // Smoothly shift board-comment leftward to align with grid start
      to(commentBoard, { x: () => -getCommentCenterOffsetX(), duration: 0.7 }, "<");
    }

    if (!boardInsight) {
      to(sidePanel, { duration: 2 }, "<");
    }

    // board-insight expands
    set(boardInsight, { pointerEvents: "auto" }, "<");
    if (isMobile()) {
      to(boardInsight, { autoAlpha: 1, y: 0, duration: 0.7 }, "<+0.2");
    } else {
      to(boardInsight, {
        autoAlpha: 1,
        y: 0,
        height: getBoardInsightHeight,
        paddingTop: getBoardInsightPaddingY,
        paddingBottom: getBoardInsightPaddingY,
        duration: 0.7,
      }, "<+0.1");
    }
    to(boardInsight, { duration: 2 }, "<");

    ScrollTrigger.refresh();

    // Resize handling
    let suppressRefresh = false;
    const onFullscreen = () => {
      suppressRefresh = true;
      clearTimeout(onFullscreen._timer);
      onFullscreen._timer = setTimeout(() => { suppressRefresh = false; }, 1000);
    };
    document.addEventListener("fullscreenchange", onFullscreen);
    document.addEventListener("webkitfullscreenchange", onFullscreen);

    window.addEventListener("resize", debounce(() => {
      syncSectionHeight();
      positionBubbles(bubbles);
      if (!suppressRefresh) {
        ScrollTrigger.refresh();
      }
    }, 150));

    setupBubbleDialog(bubbles);
    setupFormSubmit();
  }

  // ─── Form submit ──────────────────────────────────────────────────────────────

  function setupFormSubmit() {
    const form = storySection.querySelector(".headline");
    if (!form) return;

    const textarea = form.querySelector(".story-prompt-placeholder");
    const checkbox = form.querySelector("input[name='submission-consent']");
    const submitBtn = form.querySelector("button[type='submit']");
    if (!textarea || !submitBtn) return;
    const registerDialog = document.querySelector('quick-register-for-story-dialog')

    // ── Toast helper ──
    function showToast(msg, isError) {
      let toast = storySection.querySelector(".bfm-story-toast");
      if (!toast) {
        toast = document.createElement("div");
        toast.className = "bfm-story-toast";
        Object.assign(toast.style, {
          position: "fixed",
          bottom: "32px",
          left: "50%",
          transform: "translateX(-50%)",
          padding: "12px 24px",
          borderRadius: "99px",
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: "600",
          color: "#fff",
          zIndex: "9999",
          pointerEvents: "none",
          transition: "opacity 0.3s ease",
          whiteSpace: "nowrap",
        });
        document.body.appendChild(toast);
      }
      toast.textContent = msg;
      toast.style.background = isError ? "#c0392b" : "#6a2b3a";
      toast.style.opacity = "1";
      clearTimeout(toast._hideTimer);
      toast._hideTimer = setTimeout(() => { toast.style.opacity = "0"; }, 3000);
    }

    // ── Submit handler ──
    form.addEventListener("submit", async function (e) {
      e.preventDefault();

      const content = textarea.value.trim();
      if (!content) {
        textarea.focus();
        return;
      }

      if (checkbox && !checkbox.checked) {
        const consentEl = form.querySelector(".headline-consent");
        if (consentEl) checkAni(consentEl, '#c0392b');
        return;
      }

      const activityCode = form.dataset.activityCode || "";
      const shareAgreed = checkbox ? checkbox.checked : false;

      submitBtn.disabled = true;

      try {
        const baseUrl = (window.__MC__CONFIG__ && window.__MC__CONFIG__.apiUrl) || "";
        const email = window.__MOMCOZY_USER_INFO__?.email || undefined;
        const token = window.__MOMCOZY_USER_INFO__?.token || undefined;
        const resp = await fetch(baseUrl + "/api/station/activity/breastfeeding-2026/messages/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { "Authorization": "Bearer " + token } : {}),
            ...window.__MC__CONFIG__.headers
          },
          credentials: "include",

          body: JSON.stringify({ activityCode, content, shareAgreed }),
        });

        const json = await resp.json();

        if (json.code === "200" || json.code === "0" || resp.ok) {
          const result = json.result || {};
          textarea.value = "";

          // Store messageCode for post-registration use
          if (result.messageCode) {
            window.__BFM_PENDING_MESSAGE_CODES__.push(result.messageCode);
          }

          // 打开成功弹窗
          if (registerDialog) {
            registerDialog.open({
              activityCode: activityCode,
              activityType: "QuickRegistration_26Breastfeeding",
              isLoggedIn: !!window.__MOMCOZY_USER_INFO__?.email
            });
          }
        } else {
          showToast(json.message || "Submission failed, please try again.", true);
        }
      } catch (err) {
        console.error("[bfm-story-animate] submit error", err);
        showToast("Network error, please try again.", true);
      } finally {
        submitBtn.disabled = false;
      }
    });
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────

  const boot = () => document.fonts.ready.then(initStoryAnimate);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

})();
